import React, { useEffect, useMemo, useState } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert } from "reactstrap"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useMutation, useQuery } from "@apollo/client"
import gql from "graphql-tag"
import Select from "react-select"
import * as yup from "yup"
import Loader from "../../Material/Loader"
import { AdjusterSchema } from "../../Incidents/IncidentModals/IncidentModalsSchema"

const TPAAdjusterFormSchema = AdjusterSchema.shape({
    tpaId: yup.string().required("TPA is required"),
})

const GetTPAAdjusterQuery = gql`
  query GetTPAAdjuster($id: ID!) {
    tpaAdjuster(id: $id) {
      id
      name
      phone
      email
      role
      tpa {
        id
        name
      }
    }
  }
`

const AllTPAsQuery = gql`
  query AllTPAs {
    allTpas {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`

const CreateTPAAdjusterMutation = gql`
  mutation CreateTPAAdjuster($input: CreateTPAAdjusterMutationInput!) {
    createTpaAdjuster(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const UpdateTPAAdjusterMutation = gql`
  mutation UpdateTPAAdjuster($input: UpdateTPAAdjusterMutationInput!) {
    updateTpaAdjuster(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const CreateUpdateTPAAdjusterModal = ({ isOpen, toggle, id, refetchQuery }) => {
    const [mutationError, setMutationError] = useState(null)
    const isUpdate = !!id
    const { data: adjusterData, loading: adjusterLoading } = useQuery(GetTPAAdjusterQuery, {
        variables: { id },
        skip: !isUpdate,
        fetchPolicy: "network-only"
    })
    const { data: tpaData, loading: tpaLoading } = useQuery(AllTPAsQuery, { fetchPolicy: "network-only" })
    const [createAdjuster, { loading: creating }] = useMutation(CreateTPAAdjusterMutation)
    const [updateAdjuster, { loading: updating }] = useMutation(UpdateTPAAdjusterMutation)

    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        resolver: yupResolver(TPAAdjusterFormSchema)
    })

    const tpaOptions = useMemo(() => (
        tpaData?.allTpas?.edges?.map(({ node }) => ({
            value: node.id,
            label: node.name
        })) || []
    ), [tpaData])

    useEffect(() => {
        if (isUpdate && adjusterData?.tpaAdjuster) {
            reset({
                tpaId: adjusterData.tpaAdjuster.tpa?.id || "",
                name: adjusterData.tpaAdjuster.name || "",
                phone: adjusterData.tpaAdjuster.phone || "",
                email: adjusterData.tpaAdjuster.email || "",
                role: adjusterData.tpaAdjuster.role || "",
            })
        } else {
            reset({
                tpaId: "",
                name: "",
                phone: "",
                email: "",
                role: "",
            })
        }
    }, [adjusterData, isUpdate, reset])

    const onSubmit = async (formData) => {
        try {
            setMutationError(null)
            const payload = {
                ...formData,
                phone: formData.phone || null,
                email: formData.email || null,
                role: formData.role || null,
            }
            const result = isUpdate
                ? await updateAdjuster({ variables: { input: { ...payload, id } } })
                : await createAdjuster({ variables: { input: payload } })
            const mutationResult = result?.data?.createTpaAdjuster || result?.data?.updateTpaAdjuster
            if (mutationResult?.ok) {
                toggle()
                if (refetchQuery) await refetchQuery()
            } else if (mutationResult?.errors?.length) {
                setMutationError(mutationResult.errors.map(e => e.messages).flat().join(", "))
            } else {
                setMutationError("Unknown error occurred.")
            }
        } catch (error) {
            setMutationError(error.message || "Unknown error occurred.")
        }
    }

    const loading = adjusterLoading || tpaLoading

    return (
        <Modal className="tpa-modal modal-dialog-centered" isOpen={isOpen} size="lg">
            <ModalHeader>{isUpdate ? "Update TPA Adjuster" : "Create TPA Adjuster"}</ModalHeader>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <ModalBody>
                    {loading ? <Loader /> : (
                        <>
                            <FormGroup>
                                <Label for="tpaId">TPA <span className="text-danger">*</span></Label>
                                <Controller
                                    name="tpaId"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <Select
                                            options={tpaOptions}
                                            placeholder="Select TPA"
                                            className={errors.tpaId ? "is-invalid" : ""}
                                            value={tpaOptions.find(opt => opt.value === field.value) || null}
                                            onChange={opt => field.onChange(opt ? opt.value : "")}
                                        />
                                    )}
                                />
                                {errors.tpaId && <div className="text-danger">{errors.tpaId.message}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label for="name">Name <span className="text-danger">*</span></Label>
                                <Controller
                                    name="name"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <Input id="name" value={field.value ?? ""} onChange={e => field.onChange(e.target.value)} invalid={!!errors.name} />
                                    )}
                                />
                                {errors.name && <div className="text-danger">{errors.name.message}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label for="phone">Phone</Label>
                                <Controller
                                    name="phone"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <Input id="phone" value={field.value ?? ""} onChange={e => field.onChange(e.target.value)} invalid={!!errors.phone} />
                                    )}
                                />
                                {errors.phone && <div className="text-danger">{errors.phone.message}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label for="email">Email</Label>
                                <Controller
                                    name="email"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <Input id="email" type="email" value={field.value ?? ""} onChange={e => field.onChange(e.target.value)} invalid={!!errors.email} />
                                    )}
                                />
                                {errors.email && <div className="text-danger">{errors.email.message}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label for="role">Role</Label>
                                <Controller
                                    name="role"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <Input id="role" value={field.value ?? ""} onChange={e => field.onChange(e.target.value)} invalid={!!errors.role} />
                                    )}
                                />
                                {errors.role && <div className="text-danger">{errors.role.message}</div>}
                            </FormGroup>
                            {mutationError && <Alert color="danger">{mutationError}</Alert>}
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>Cancel</Button>
                    <Button color="primary" type="submit" disabled={creating || updating}>{isUpdate ? "Update" : "Create"}</Button>
                </ModalFooter>
            </Form>
        </Modal>
    )
}

export default CreateUpdateTPAAdjusterModal
